import { useState } from 'react';

import {
  AS_STATE_LABEL,
  ErrorBox,
  Spinner,
  formatDate,
  formatPhone,
  useAsync,
} from '@solar/ui';

import { api } from '../api';

/** 레거시 AsList 와 같은 구분 — 전체 / 미배정(1) / 배정(2) / 완료(3). */
const FILTERS: Array<{ value: number | undefined; label: string }> = [
  { value: undefined, label: '전체' },
  { value: 1, label: '미배정' },
  { value: 2, label: '배정' },
  { value: 3, label: '완료' },
];

const SIZE = 30;

export function AsTable({
  selected,
  onSelect,
  version,
  initialState,
}: {
  selected: number | null;
  onSelect: (id: number) => void;
  /** 상세에서 배정/완료가 일어나면 올라간다 — 목록 재조회 트리거. */
  version: number;
  /** 대시보드 카드에서 넘어올 때의 초기 상태 필터 (1 미배정 / 2 배정 / 3 완료). */
  initialState?: number;
}) {
  const [state, setState] = useState<number | undefined>(initialState);
  const [keyword, setKeyword] = useState('');
  const [applied, setApplied] = useState('');
  const [page, setPage] = useState(1);

  const list = useAsync(
    () => api.listAs({ state, keyword: applied, page, size: SIZE }),
    [state, applied, page, version],
  );

  function changeFilter(value: number | undefined) {
    setState(value);
    setPage(1);
  }

  const total = list.data?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / SIZE));

  return (
    <>
      <div className="toolbar">
        {FILTERS.map((f) => (
          <button
            key={f.label}
            type="button"
            className={state === f.value ? 'chip active' : 'chip'}
            onClick={() => changeFilter(f.value)}
          >
            {f.label}
          </button>
        ))}

        <form
          className="search"
          onSubmit={(e) => {
            e.preventDefault();
            setApplied(keyword.trim());
            setPage(1);
          }}
        >
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="이름 · 연락처 · 주소"
          />
          <button type="submit">검색</button>
        </form>
      </div>

      {list.loading && <Spinner />}
      {list.error && <ErrorBox message={list.error} onRetry={list.reload} />}

      {list.data && (
        <table className="grid">
          <thead>
            <tr>
              <th>고객</th>
              <th>연락처</th>
              <th>주소</th>
              <th>접수내용</th>
              <th>상태</th>
              <th>담당기사</th>
              {/* 레거시 CASE WHEN 그대로 — 미배정=접수일, 배정=예정일, 완료=완료일. */}
              <th>표시일자</th>
            </tr>
          </thead>
          <tbody>
            {list.data.items.length === 0 && (
              <tr>
                <td colSpan={7} className="muted">
                  조건에 맞는 A/S 건이 없습니다.
                </td>
              </tr>
            )}
            {list.data.items.map((item) => (
              <tr
                key={item.id}
                className={item.id === selected ? 'selected' : ''}
                onClick={() => onSelect(item.id)}
              >
                <td>{item.user_name ?? `#${item.build_order_id}`}</td>
                <td>{formatPhone(item.user_phone)}</td>
                <td className="addr">{item.addr_full ?? '-'}</td>
                <td className="addr">{item.etc ?? '-'}</td>
                <td>
                  <span className={`badge a${item.state}`}>
                    {AS_STATE_LABEL[item.state] ?? item.state}
                  </span>
                </td>
                <td>{item.engineer_name ?? '-'}</td>
                <td>{formatDate(item.state_date)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="pager">
        <button type="button" onClick={() => setPage((p) => p - 1)} disabled={page <= 1}>
          이전
        </button>
        <span>
          {page} / {lastPage} (총 {total}건)
        </span>
        <button type="button" onClick={() => setPage((p) => p + 1)} disabled={page >= lastPage}>
          다음
        </button>
      </div>
    </>
  );
}
